import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  NumberInput,
  NumberInputField,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  Badge,
  Tooltip,
  useDisclosure,
  useToast,
  TableContainer,
} from "@chakra-ui/react";
import {
  FaPlus,
  FaShoppingCart,
  FaTrash,
  FaGasPump,
  FaBoxOpen,
  FaCalculator,
} from "react-icons/fa";
import {
  SESSION_KEYS,
  readSessionJson,
  normalizeProdutosShellList,
} from "../utils/atendimentoSessionCache";
import SimulacaoCustoFlutuante from "./simulacao-custo-flutuante";

const API_PRECOS = "/api/frota/historico-precos";

function todayISO() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function todayBR() {
  const [y, m, d] = todayISO().split("-");
  return `${d}/${m}/${y}`;
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "R$ 0,0000";
  return `R$ ${n.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  })}`;
}

function formatDate(str) {
  if (!str) return "–";
  const part = String(str).split(/[-T]/);
  if (part.length < 3) return str;
  const [y, m, d] = part;
  return `${d}/${m}/${y}`;
}

function isoToBR(iso) {
  if (!iso) return "–";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}/${y}`;
}

/** Preços do dia (dataRef) por produto + fornecedor */
export function buildCombustivelCatalog(rows, dataRef = todayISO()) {
  if (!Array.isArray(rows)) return [];
  const hoje = String(dataRef).slice(0, 10);
  const byKey = {};
  rows.forEach((row) => {
    const data = String(row.data || "").slice(0, 10);
    if (data !== hoje) return;
    const produtoId = row.produto_id;
    const fornecedorId = row.fornecedor_id;
    if (!produtoId) return;
    const key = `${produtoId}|${fornecedorId || ""}`;
    const prev = byKey[key];
    if (!prev || String(row.created_at || "") > String(prev.created_at || "")) {
      byKey[key] = row;
    }
  });

  return Object.values(byKey)
    .map((row) => {
      const nome =
        row.produtos?.nome ||
        row.produto_nome ||
        row.produto_id ||
        "Combustível";
      const fornecedor = row.fornecedores?.nome || row.fornecedor_nome || "–";
      return {
        key: `${row.produto_id}|${row.fornecedor_id}`,
        codigo: String(row.produto_id),
        descricao: nome,
        precoUnitario: Number(row.valor) || 0,
        fornecedor,
        fornecedorId: row.fornecedor_id,
        dataPreco: row.data,
        origem: "combustivel",
        um: "L",
        tipo: "COMBUSTIVEL",
      };
    })
    .sort((a, b) =>
      String(a.descricao).localeCompare(String(b.descricao), "pt-BR"),
    );
}

export function buildShellCatalog(rows) {
  const list = normalizeProdutosShellList(rows);
  return list
    .map((item) => ({
      key: `shell-${item.codigo}`,
      codigo: String(item.codigo),
      descricao: item.descricao || item.nome || item.codigo,
      precoUnitario: null,
      fornecedor: "",
      origem: "shell",
      um: item.unidade || "UN",
      tipo: item.tipo || "PA",
      estoque: item.estoque,
      ncm: item.ncm,
    }))
    .sort((a, b) =>
      String(a.descricao).localeCompare(String(b.descricao), "pt-BR"),
    );
}

/** Cada linha tem dois NumberInput; renderizar o catálogo inteiro trava a digitação. */
const MAX_LINHAS_SHELL = 50;

function lineTotal(item) {
  const q = Number(item.quantidade) || 0;
  const p = Number(item.precoUnitario) || 0;
  const d = Number(item.desconto) || 0;
  return q * p * (1 - d / 100);
}

const CarrinhoProdutosAtendimento = ({
  valoresSelecionados = [],
  onChangeCarrinho,
}) => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [loading, setLoading] = useState(true);
  const [combustiveis, setCombustiveis] = useState([]);
  const [outros, setOutros] = useState([]);
  const [filtroComb, setFiltroComb] = useState("");
  const [filtroShell, setFiltroShell] = useState("");
  const [qtyComb, setQtyComb] = useState({});
  const [qtyShell, setQtyShell] = useState({});
  const [precoShell, setPrecoShell] = useState({});
  /** Data de referência do preço de custo (histórico) */
  const [dataPrecoRef, setDataPrecoRef] = useState(() => todayISO());
  /** Preços simulados por key do catálogo (custo → preço final) */
  const [precosSimulados, setPrecosSimulados] = useState({});
  const [produtoSimulacao, setProdutoSimulacao] = useState(null);

  const abrirSimulacao = (produto, opts = {}) => {
    const catalogKey = produto.key || null;
    setProdutoSimulacao({
      key: catalogKey || produto.cartKey,
      catalogKey,
      cartKey: produto.cartKey || null,
      cartIndex: opts.cartIndex ?? null,
      codigo: produto.codigo,
      descricao: produto.descricao,
      fornecedor: produto.fornecedor || "",
      fornecedorId: produto.fornecedorId,
      origem: produto.origem || "combustivel",
      tipo: produto.tipo,
      um: produto.um || "L",
      dataPreco: produto.dataPreco || dataPrecoRef || null,
      quantidade:
        opts.quantidade ?? qtyComb[catalogKey] ?? qtyComb[produto.key] ?? 1,
      dataPrecoRef,
      precoCusto:
        produto.precoCusto ??
        produto.precoCustoOriginal ??
        (produto.origem === "combustivel" ? produto.precoUnitario : null) ??
        produto.precoUnitario,
      precoUnitario: produto.precoUnitario,
      simulacao:
        produto.simulacao ||
        precosSimulados[catalogKey || produto.key]?.simulacao,
    });
    onOpen();
  };

  const loadCatalogos = useCallback(async () => {
    setLoading(true);
    const dataRef = dataPrecoRef || todayISO();
    try {
      const res = await fetch(
        `${API_PRECOS}?de=${encodeURIComponent(dataRef)}&ate=${encodeURIComponent(dataRef)}`,
      );
      if (!res.ok) {
        throw new Error(
          `Erro ao carregar preços de combustíveis de ${isoToBR(dataRef)}`,
        );
      }
      const data = await res.json();
      const precosRows = Array.isArray(data) ? data : [];
      setCombustiveis(buildCombustivelCatalog(precosRows, dataRef));

      const shellCached = readSessionJson(SESSION_KEYS.PRODUTOS_SHELL, []);
      setOutros(buildShellCatalog(shellCached));
    } catch (err) {
      console.error(err);
      toast({
        title: "Erro ao carregar produtos",
        description: err.message,
        status: "error",
        isClosable: true,
      });
      setCombustiveis([]);
    } finally {
      setLoading(false);
    }
  }, [toast, dataPrecoRef]);

  useEffect(() => {
    loadCatalogos();
  }, [loadCatalogos]);

  useEffect(() => {
    // Ao mudar a data de preço, limpa simulações anteriores (custos mudaram)
    setPrecosSimulados({});
  }, [dataPrecoRef]);

  const handleDataPrecoChange = (e) => {
    const next = e.target.value || todayISO();
    setDataPrecoRef(next);
  };

  const updateCart = (next) => {
    onChangeCarrinho?.(next);
  };

  const aplicarSimulacao = ({
    precoFinal,
    precoCusto,
    simulacao,
    dataPreco,
  }) => {
    const key = produtoSimulacao?.key;
    if (!key || !produtoSimulacao) return;

    const catalogKey = produtoSimulacao.catalogKey || key;
    const dataUsada =
      dataPreco || produtoSimulacao.dataPreco || dataPrecoRef || todayISO();

    setPrecosSimulados((prev) => ({
      ...prev,
      [catalogKey]: { precoFinal, precoCusto, simulacao, dataPreco: dataUsada },
    }));

    const applyToItem = (item) => {
      const updated = {
        ...item,
        precoCustoOriginal: precoCusto,
        precoUnitario: precoFinal,
        dataPreco: dataUsada,
        simulacao,
      };
      updated.precoTotal = lineTotal(updated);
      return updated;
    };

    const cartIndex = produtoSimulacao.cartIndex;
    if (cartIndex != null && cartIndex >= 0) {
      updateCart(
        valoresSelecionados.map((item, i) =>
          i === cartIndex ? applyToItem(item) : item,
        ),
      );
      toast({
        title: "Preço do item atualizado",
        description: `${produtoSimulacao.descricao}: R$ ${Number(
          precoFinal,
        ).toLocaleString("pt-BR", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })}`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const cartKey = `combustivel-${produtoSimulacao.codigo}-${produtoSimulacao.fornecedorId || ""}`;
    const existingIdx = valoresSelecionados.findIndex(
      (i) => i.cartKey === cartKey || i.cartKey === produtoSimulacao.cartKey,
    );

    if (existingIdx >= 0) {
      updateCart(
        valoresSelecionados.map((item, i) =>
          i === existingIdx ? applyToItem(item) : item,
        ),
      );
      toast({
        title: "Preço do item atualizado",
        description: `${produtoSimulacao.descricao}: R$ ${Number(
          precoFinal,
        ).toLocaleString("pt-BR", {
          minimumFractionDigits: 4,
          maximumFractionDigits: 4,
        })}`,
        status: "success",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    // Novo item: adiciona direto ao carrinho com o preço simulado
    const qtd = Number(produtoSimulacao.quantidade) || 1;
    addItem(
      {
        key: catalogKey,
        codigo: produtoSimulacao.codigo,
        descricao: produtoSimulacao.descricao,
        tipo: produtoSimulacao.tipo || "COMBUSTIVEL",
        um: produtoSimulacao.um || "L",
        fornecedor: produtoSimulacao.fornecedor || "",
        fornecedorId: produtoSimulacao.fornecedorId,
        origem: produtoSimulacao.origem || "combustivel",
        dataPreco: dataUsada,
        precoUnitario: precoCusto,
      },
      qtd,
      precoFinal,
      { precoCusto, simulacao },
    );
  };

  const addItem = (catalogItem, quantidade, precoUnitario, extras = {}) => {
    const q = Number(quantidade);
    const p = Number(precoUnitario);
    if (!q || q <= 0) {
      toast({
        title: "Informe a quantidade",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }
    if (!Number.isFinite(p) || p < 0) {
      toast({
        title: "Informe um preço válido",
        status: "warning",
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const cartKey = `${catalogItem.origem}-${catalogItem.codigo}-${catalogItem.fornecedorId || ""}`;
    const existingIdx = valoresSelecionados.findIndex(
      (i) => i.cartKey === cartKey,
    );

    let next;
    if (existingIdx >= 0) {
      next = valoresSelecionados.map((item, idx) => {
        if (idx !== existingIdx) return item;
        const quantidadeNova = Number(item.quantidade) + q;
        const updated = {
          ...item,
          quantidade: quantidadeNova,
          precoUnitario: p,
          precoCustoOriginal:
            extras.precoCusto ??
            item.precoCustoOriginal ??
            catalogItem.precoUnitario,
          simulacao: extras.simulacao ?? item.simulacao,
        };
        updated.precoTotal = lineTotal(updated);
        return updated;
      });
    } else {
      const novo = {
        cartKey,
        codigo: catalogItem.codigo,
        descricao: catalogItem.descricao,
        tipo: catalogItem.tipo,
        um: catalogItem.um,
        quantidade: q,
        precoUnitario: p,
        precoCustoOriginal:
          extras.precoCusto ?? catalogItem.precoUnitario ?? null,
        desconto: 0,
        fornecedor: catalogItem.fornecedor || "",
        origem: catalogItem.origem,
        dataPreco: catalogItem.dataPreco || null,
        simulacao: extras.simulacao || null,
        precoTotal: 0,
      };
      novo.precoTotal = lineTotal(novo);
      next = [...valoresSelecionados, novo];
    }

    updateCart(next);
    toast({
      title: "Produto adicionado",
      status: "success",
      duration: 1800,
      isClosable: true,
    });
  };

  const removeItem = (index) => {
    updateCart(valoresSelecionados.filter((_, i) => i !== index));
  };

  const updateItemField = (index, field, value) => {
    const next = valoresSelecionados.map((item, i) => {
      if (i !== index) return item;
      const updated = { ...item, [field]: value };
      updated.precoTotal = lineTotal(updated);
      return updated;
    });
    updateCart(next);
  };

  const totalGeral = useMemo(
    () => valoresSelecionados.reduce((s, item) => s + lineTotal(item), 0),
    [valoresSelecionados],
  );

  const combustiveisFiltrados = useMemo(() => {
    const f = filtroComb.trim().toLowerCase();
    if (!f) return combustiveis;
    return combustiveis.filter(
      (p) =>
        p.descricao.toLowerCase().includes(f) ||
        p.fornecedor.toLowerCase().includes(f),
    );
  }, [combustiveis, filtroComb]);

  const shellFiltradosTodos = useMemo(() => {
    const f = filtroShell.trim().toLowerCase();
    if (!f) return outros;
    return outros.filter(
      (p) =>
        p.descricao.toLowerCase().includes(f) ||
        p.codigo.toLowerCase().includes(f),
    );
  }, [outros, filtroShell]);

  const shellFiltrados = useMemo(
    () => shellFiltradosTodos.slice(0, MAX_LINHAS_SHELL),
    [shellFiltradosTodos],
  );

  if (loading) {
    return (
      <Center py={16}>
        <Spinner size="xl" color="#007abe" />
      </Center>
    );
  }

  return (
    <>
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        border="1px solid"
        borderColor="gray.200"
        overflow="hidden"
      >
        <Flex
          align="center"
          justify="space-between"
          px={5}
          py={4}
          bgGradient="linear(to-r, #007abe, #005a8a)"
          color="white"
          flexWrap="wrap"
          gap={3}
        >
          <HStack>
            <Icon as={FaShoppingCart} boxSize={5} />
            <Box>
              <Heading size="md">Carrinho de produtos</Heading>
              <Text fontSize="xs" opacity={0.9}>
                Custo de {isoToBR(dataPrecoRef)} → simule o preço de venda ·
                Shell CRM
              </Text>
            </Box>
          </HStack>
          <Badge colorScheme="yellow" fontSize="md" px={3} py={1}>
            Total: {formatMoney(totalGeral)}
          </Badge>
        </Flex>

        <Box p={{ base: 4, md: 5 }}>
          <Tabs colorScheme="blue" variant="enclosed" mb={6} isLazy>
            <TabList>
              <Tab>
                <Icon as={FaGasPump} mr={2} />
                Combustíveis — {isoToBR(dataPrecoRef)} ({combustiveis.length})
              </Tab>
              <Tab>
                <Icon as={FaBoxOpen} mr={2} />
                Outros produtos ({outros.length})
              </Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                <Flex
                  gap={3}
                  mb={3}
                  direction={{ base: "column", md: "row" }}
                  align={{ base: "stretch", md: "flex-end" }}
                  flexWrap="wrap"
                >
                  <FormControl maxW={{ base: "100%", md: "200px" }}>
                    <FormLabel fontSize="xs" color="#007abe">
                      Data do preço (custo)
                    </FormLabel>
                    <Input
                      size="sm"
                      type="date"
                      value={dataPrecoRef}
                      max={todayISO()}
                      onChange={handleDataPrecoChange}
                    />
                  </FormControl>
                  <FormControl flex="1" maxW={{ base: "100%", md: "360px" }}>
                    <FormLabel fontSize="xs" color="#007abe">
                      Buscar combustível / fornecedor
                    </FormLabel>
                    <Input
                      size="sm"
                      value={filtroComb}
                      onChange={(e) => setFiltroComb(e.target.value)}
                      placeholder="Ex.: S10, diesel, fornecedor..."
                    />
                  </FormControl>
                </Flex>
                {combustiveisFiltrados.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    Nenhum preço de combustível registrado para{" "}
                    {isoToBR(dataPrecoRef)}. Cadastre em Histórico de Preços ou
                    escolha outra data.
                  </Text>
                ) : (
                  <TableContainer
                    maxH="320px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="md"
                  >
                    <Table size="sm">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th>Produto</Th>
                          <Th>Fornecedor</Th>
                          <Th>Data preço</Th>
                          <Th isNumeric>Custo</Th>
                          <Th isNumeric>Preço simulado</Th>
                          <Th w="110px">Qtd</Th>
                          <Th w="140px" />
                        </Tr>
                      </Thead>
                      <Tbody>
                        {combustiveisFiltrados.map((p) => {
                          const sim = precosSimulados[p.key];
                          const precoAdd = sim?.precoFinal ?? p.precoUnitario;
                          return (
                            <Tr key={p.key} bg={sim ? "green.50" : undefined}>
                              <Td fontWeight="medium">{p.descricao}</Td>
                              <Td fontSize="sm">{p.fornecedor}</Td>
                              <Td fontSize="sm">{formatDate(p.dataPreco)}</Td>
                              <Td isNumeric fontSize="sm" color="gray.600">
                                {formatMoney(p.precoUnitario)}
                              </Td>
                              <Td
                                isNumeric
                                fontWeight="semibold"
                                color="#007abe"
                              >
                                {sim ? (
                                  <Tooltip
                                    label={`KM ${sim.simulacao?.kilometragem} · ${
                                      sim.simulacao?.tipoVeiculo === "truck"
                                        ? "Truck"
                                        : "Toco"
                                    }`}
                                  >
                                    <Text as="span">
                                      {formatMoney(sim.precoFinal)}
                                    </Text>
                                  </Tooltip>
                                ) : (
                                  <Text
                                    as="span"
                                    color="gray.400"
                                    fontWeight="normal"
                                  >
                                    —
                                  </Text>
                                )}
                              </Td>
                              <Td>
                                <NumberInput
                                  size="sm"
                                  min={0}
                                  step={1}
                                  value={qtyComb[p.key] ?? 1}
                                  onChange={(v) =>
                                    setQtyComb((prev) => ({
                                      ...prev,
                                      [p.key]: v,
                                    }))
                                  }
                                >
                                  <NumberInputField />
                                </NumberInput>
                              </Td>
                              <Td>
                                <HStack spacing={1}>
                                  <Tooltip label="Simular preço de venda">
                                    <IconButton
                                      size="xs"
                                      aria-label="Simular custos"
                                      icon={<FaCalculator />}
                                      variant="outline"
                                      colorScheme="blue"
                                      onClick={() =>
                                        abrirSimulacao({
                                          ...p,
                                          precoCusto: p.precoUnitario,
                                        })
                                      }
                                    />
                                  </Tooltip>
                                  <Tooltip label="Adicionar ao carrinho">
                                    <IconButton
                                      size="xs"
                                      aria-label="Adicionar ao carrinho"
                                      icon={<FaPlus />}
                                      bg="#007abe"
                                      color="white"
                                      _hover={{ bg: "#0066a3" }}
                                      onClick={() =>
                                        addItem(
                                          p,
                                          qtyComb[p.key] ?? 1,
                                          precoAdd,
                                          {
                                            precoCusto: p.precoUnitario,
                                            simulacao: sim?.simulacao || null,
                                          },
                                        )
                                      }
                                    />
                                  </Tooltip>
                                </HStack>
                              </Td>
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

              <TabPanel px={0}>
                <FormControl mb={3} maxW="360px">
                  <FormLabel fontSize="xs" color="#007abe">
                    Buscar produto
                  </FormLabel>
                  <Input
                    size="sm"
                    value={filtroShell}
                    onChange={(e) => setFiltroShell(e.target.value)}
                    placeholder="Código ou descrição..."
                  />
                </FormControl>
                {shellFiltradosTodos.length > MAX_LINHAS_SHELL && (
                  <Text fontSize="xs" color="gray.500" mb={2}>
                    Exibindo {MAX_LINHAS_SHELL} de {shellFiltradosTodos.length}{" "}
                    produtos. Refine a busca para encontrar outros.
                  </Text>
                )}
                {shellFiltrados.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">
                    {outros.length === 0
                      ? "Nenhum produto Shell no cache. Reabra o atendimento para carregar INT_SHELL_CRM_PRODUTO."
                      : "Nenhum produto encontrado para essa busca."}
                  </Text>
                ) : (
                  <TableContainer
                    maxH="320px"
                    overflowY="auto"
                    border="1px solid"
                    borderColor="gray.100"
                    borderRadius="md"
                  >
                    <Table size="sm">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th>Código</Th>
                          <Th>Descrição</Th>
                          <Th>Un.</Th>
                          <Th w="120px">Preço</Th>
                          <Th w="100px">Qtd</Th>
                          <Th w="90px" />
                        </Tr>
                      </Thead>
                      <Tbody>
                        {shellFiltrados.map((p) => (
                          <Tr key={p.key}>
                            <Td fontFamily="mono" fontSize="xs">
                              {p.codigo}
                            </Td>
                            <Td fontSize="sm">{p.descricao}</Td>
                            <Td>{p.um}</Td>
                            <Td>
                              <NumberInput
                                size="sm"
                                min={0}
                                step={0.0001}
                                precision={4}
                                value={precoShell[p.key] ?? ""}
                                onChange={(v) =>
                                  setPrecoShell((prev) => ({
                                    ...prev,
                                    [p.key]: v,
                                  }))
                                }
                              >
                                <NumberInputField placeholder="0,0000" />
                              </NumberInput>
                            </Td>
                            <Td>
                              <NumberInput
                                size="sm"
                                min={0}
                                step={1}
                                value={qtyShell[p.key] ?? 1}
                                onChange={(v) =>
                                  setQtyShell((prev) => ({
                                    ...prev,
                                    [p.key]: v,
                                  }))
                                }
                              >
                                <NumberInputField />
                              </NumberInput>
                            </Td>
                            <Td>
                              <Tooltip label="Adicionar ao carrinho">
                                <IconButton
                                  size="xs"
                                  aria-label="Adicionar ao carrinho"
                                  icon={<FaPlus />}
                                  bg="#007abe"
                                  color="white"
                                  _hover={{ bg: "#0066a3" }}
                                  onClick={() =>
                                    addItem(
                                      p,
                                      qtyShell[p.key] ?? 1,
                                      precoShell[p.key],
                                    )
                                  }
                                />
                              </Tooltip>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
            <Flex justify="space-between" align="center" mb={3}>
              <Heading size="sm" color="#007abe">
                Itens do carrinho ({valoresSelecionados.length})
              </Heading>
              <Text fontWeight="bold" color="green.600">
                {formatMoney(totalGeral)}
              </Text>
            </Flex>

            {valoresSelecionados.length === 0 ? (
              <Text color="gray.500" fontSize="sm">
                Nenhum produto no carrinho.
              </Text>
            ) : (
              <TableContainer overflowX="auto">
                <Table size="sm" variant="simple">
                  <Thead bg="#007abe">
                    <Tr>
                      <Th color="white">Produto</Th>
                      <Th color="white">Origem</Th>
                      <Th color="white" isNumeric>
                        Qtd
                      </Th>
                      <Th color="white" isNumeric>
                        Preço un.
                      </Th>
                      <Th color="white" isNumeric>
                        Desc. %
                      </Th>
                      <Th color="white" isNumeric>
                        Total
                      </Th>
                      <Th color="white" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {valoresSelecionados.map((item, index) => (
                      <Tr key={item.cartKey || `${item.codigo}-${index}`}>
                        <Td verticalAlign="middle" py={3}>
                          <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                            {item.descricao}
                          </Text>
                          <HStack spacing={2} mt={1} flexWrap="wrap">
                            {item.fornecedor && (
                              <Text fontSize="xs" color="gray.500">
                                {item.fornecedor}
                              </Text>
                            )}
                            {item.um && (
                              <Text fontSize="xs" color="gray.500">
                                {item.um}
                              </Text>
                            )}
                            {item.simulacao && (
                              <Badge colorScheme="green" fontSize="0.65rem">
                                Simulado · {item.simulacao.kilometragem} km ·{" "}
                                {item.simulacao.tipoVeiculo === "truck"
                                  ? "truck"
                                  : "toco"}
                              </Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td verticalAlign="middle">
                          <Badge
                            colorScheme={
                              item.origem === "combustivel"
                                ? "orange"
                                : "purple"
                            }
                          >
                            {item.origem === "combustivel"
                              ? "Combustível"
                              : "Shell"}
                          </Badge>
                        </Td>
                        <Td isNumeric verticalAlign="middle">
                          <NumberInput
                            size="sm"
                            min={0}
                            w="90px"
                            ml="auto"
                            value={item.quantidade}
                            onChange={(v) =>
                              updateItemField(
                                index,
                                "quantidade",
                                Number(v) || 0,
                              )
                            }
                          >
                            <NumberInputField textAlign="right" />
                          </NumberInput>
                        </Td>
                        <Td isNumeric verticalAlign="middle">
                          <Box w="110px" ml="auto">
                            <NumberInput
                              size="sm"
                              min={0}
                              step={0.0001}
                              precision={4}
                              value={item.precoUnitario}
                              onChange={(v) =>
                                updateItemField(
                                  index,
                                  "precoUnitario",
                                  Number(v) || 0,
                                )
                              }
                            >
                              <NumberInputField textAlign="right" />
                            </NumberInput>
                            {item.precoCustoOriginal != null && (
                              <Text
                                fontSize="xs"
                                color="gray.500"
                                mt={1}
                                textAlign="right"
                              >
                                custo {formatMoney(item.precoCustoOriginal)}
                              </Text>
                            )}
                          </Box>
                        </Td>
                        <Td isNumeric verticalAlign="middle">
                          <NumberInput
                            size="sm"
                            min={0}
                            max={100}
                            w="80px"
                            ml="auto"
                            value={item.desconto ?? 0}
                            onChange={(v) =>
                              updateItemField(index, "desconto", Number(v) || 0)
                            }
                          >
                            <NumberInputField textAlign="right" />
                          </NumberInput>
                        </Td>
                        <Td
                          isNumeric
                          verticalAlign="middle"
                          fontWeight="semibold"
                          whiteSpace="nowrap"
                        >
                          {formatMoney(lineTotal(item))}
                        </Td>
                        <Td verticalAlign="middle" textAlign="right" w="72px">
                          <HStack spacing={1} justify="flex-end">
                            {item.origem === "combustivel" && (
                              <Tooltip label="Reabrir simulação">
                                <IconButton
                                  size="xs"
                                  aria-label="Simular"
                                  icon={<FaCalculator />}
                                  variant="ghost"
                                  colorScheme="blue"
                                  onClick={() =>
                                    abrirSimulacao(
                                      {
                                        ...item,
                                        key: item.cartKey,
                                        dataPreco:
                                          item.dataPreco || dataPrecoRef,
                                        precoCusto:
                                          item.precoCustoOriginal ??
                                          item.precoUnitario,
                                      },
                                      { cartIndex: index },
                                    )
                                  }
                                />
                              </Tooltip>
                            )}
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              aria-label="Remover"
                              icon={<FaTrash />}
                              onClick={() => removeItem(index)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Box>
      </Box>
      <SimulacaoCustoFlutuante
        isOpen={isOpen}
        onClose={onClose}
        produto={produtoSimulacao}
        onAplicar={aplicarSimulacao}
      />
    </>
  );
};

export default React.memo(CarrinhoProdutosAtendimento);
